import { GameStateListener, Mode, State, GameState, Position } from "../types";
import { GameStateManager } from "./GameStateManager";
import { SelectPieceEvent, KingGuessEvent, EventType } from "../const";

// Engine event types (will expand as needed)
export type ZodiacEngineEvent = SelectPieceEvent | KingGuessEvent;
export type ZodiacEngineEventListener = (event: ZodiacEngineEvent) => void;

export class EventManager {
  private gameId?: string;
  private gameStateListeners: Set<GameStateListener> = new Set();
  private engineEventListeners: Set<ZodiacEngineEventListener> = new Set();
  private unsubscribeDocument?: () => void;
  private gameStateManager: GameStateManager;
  private lightElo?: number;
  private darkElo?: number;
  // Flag to disable automatic remote sync (used for testing)
  public disableRemoteSync: boolean = false;

  constructor(gameStateManager: GameStateManager) {
    this.gameStateManager = gameStateManager;
    this.gameId = undefined;
  }

  // Set ELO ratings for analytics
  setLightElo(elo: number): void {
    this.lightElo = elo;
  }

  setDarkElo(elo: number): void {
    this.darkElo = elo;
  }

  getGameId(): string | undefined {
    return this.gameId;
  }

  // Listener management
  subscribeToGameStateChanges(listener: GameStateListener): void {
    this.gameStateListeners.add(listener);
  }

  unsubscribeToGameStateChanges(listener: GameStateListener): void {
    this.gameStateListeners.delete(listener);
  }

  // Notify all listeners of game state changes
  notifyLocalListeners(): void {
    this.gameStateListeners.forEach((listener) => listener());
  }

  setupDevMode() {
    if (this.unsubscribeDocument) {
      this.unsubscribeDocument();
      this.unsubscribeDocument = undefined;
    }
    this.gameStateManager.resetGame();
    this.notifyLocalListeners();
  }

  // Setup live mode and handle remote communication
  async setupLiveMode(playerId: string, assignAsLight: boolean = true): Promise<string> {

    this.gameStateManager.resetGame();
    (this.gameStateManager.getGameState() as any).repeatString = "";

    this.gameStateManager.setMode(Mode.Live);
    this.gameStateManager.setGameStateState(State.Searching);

    // Assign player color based on color balancing
    if (assignAsLight) {
      this.gameStateManager.setLightPlayer(playerId);
    } else {
      this.gameStateManager.setDarkPlayer(playerId);
    }

    // Create a new game
    this.gameId = crypto.randomUUID();

    await this.listenToDocumentChanges();

    await this.sendUpdateToRemote(); // CRITICAL: Await to ensure game exists in Firebase before matchmaking

    this.notifyLocalListeners();

    return this.gameId;
  }

  async joinGame(gameId: string, playerId: string, assignAsDark: boolean = true) {
    // Lazy import Firebase functions only when needed for multiplayer
    const { getGameStateByGameId } = await import("../../firebase/firebase");
    const { convertFromFirebaseState } = await import("../../firebase/firebaseConverter");

    const gameStateData = await getGameStateByGameId(gameId);

    if (!gameStateData) throw new Error(`Game:${gameId} doesn't exist!`);

    // CRITICAL: Validate game is not finished before loading
    // lightWon can be: '' (not finished), 'draw', true, or false
    if (gameStateData.lightWon !== '' && gameStateData.lightWon !== undefined) {
      throw new Error(`Game ${gameId} is already finished and cannot be joined`);
    }

    // reconnect logic
    var gameState = convertFromFirebaseState(gameStateData);
    if (playerId === gameState.lightPlayer || playerId === gameState.darkPlayer) {
      await this.reconnectPlayer(gameId, gameState);
      return;
    }

    // Only second player joining for the first time from here down
    this.gameId = gameId;
    this.gameStateManager.setGameState(gameState);
    (this.gameStateManager.getGameState() as any).repeatString = "";

    // Assign player colors based on color balancing
    if (assignAsDark) {
      // Standard assignment: joiner is dark, creator is light
      this.gameStateManager.setDarkPlayer(playerId);
    } else {
      // Swapped assignment: joiner is light, creator needs to be dark
      const hostId = gameState.lightPlayer || gameState.darkPlayer || '';
      if (!hostId) {
        throw new Error('Cannot determine host player ID for color swapping');
      }

      // Set joiner as light
      this.gameStateManager.setLightPlayer(playerId);
      // Set host as dark
      this.gameStateManager.setDarkPlayer(hostId);
    }

    this.gameStateManager.nextGameState();
    await this.listenToDocumentChanges();
    await this.sendUpdateToRemote();
  }

  async reconnectPlayer(gameId: string, gameState: GameState) {
    this.gameId = gameId;
    this.gameStateManager.setGameState(gameState);
    await this.listenToDocumentChanges();
    await this.sendUpdateToRemote(); // not sure if required
  }

  async listenToDocumentChanges() {
    // Lazy import Firebase functions only when needed for multiplayer
    const { listenForDocumentChanges } = await import("../../firebase/firebase");
    const { convertFromFirebaseState } = await import("../../firebase/firebaseConverter");

    this.unsubscribeDocument = listenForDocumentChanges(
      this.gameId!,
      (data: any | null) => {
        if (!data) {
          return;
        }

        const currentState = this.gameStateManager.getGameState();

        // SEPARATION OF CONCERNS: Distinguish game-changing updates from metadata-only updates.
        // The BotGameController's presence heartbeat writes lastSeen/tabHidden to Firebase every 5s.
        // Without this guard, every heartbeat triggers convertFromFirebaseState() which decodes FEN
        // and creates entirely NEW piece objects — invalidating all existing piece references.
        // Any move in progress using old references then fails silently in removePiece()
        // (which uses reference equality), causing pieces to merge instead of being captured.
        // Normalize both sides identically to avoid undefined vs "" / false mismatches
        const changes: string[] = [];
        if ((data.moveHistory?.length ?? 0) !== (currentState.fenHistory?.length ?? 0))
          changes.push(`moveHistory(${data.moveHistory?.length ?? 0} vs ${currentState.fenHistory?.length ?? 0})`);
        if ((data.isLightTurn ?? true) !== (currentState.isLightTurn ?? true))
          changes.push(`isLightTurn(${data.isLightTurn} vs ${currentState.isLightTurn})`);
        if ((data.turnCount ?? 0) !== (currentState.turnCount ?? 0))
          changes.push(`turnCount(${data.turnCount} vs ${currentState.turnCount})`);
        if ((data.state ?? "") !== (currentState.state ?? ""))
          changes.push(`state(${data.state} vs ${currentState.state})`);
        if ((data.lightKing ?? "") !== (currentState.lightKing ?? ""))
          changes.push(`lightKing(${data.lightKing} vs ${currentState.lightKing})`);
        if ((data.darkKing ?? "") !== (currentState.darkKing ?? ""))
          changes.push(`darkKing(${data.darkKing} vs ${currentState.darkKing})`);
        if ((data.lightPlayer ?? "") !== (currentState.lightPlayer ?? ""))
          changes.push(`lightPlayer(${data.lightPlayer} vs ${currentState.lightPlayer})`);
        if ((data.darkPlayer ?? "") !== (currentState.darkPlayer ?? ""))
          changes.push(`darkPlayer(${data.darkPlayer} vs ${currentState.darkPlayer})`);
        if ((data.skipTurnChange ?? false) !== (currentState.skipTurnChange ?? false))
          changes.push(`skipTurnChange(${data.skipTurnChange} vs ${currentState.skipTurnChange})`);
        if ((data.activeBonusTurnPieceId ?? "") !== (currentState.activeBonusTurnPieceId ?? ""))
          changes.push(`activeBonusTurnPieceId(${data.activeBonusTurnPieceId} vs ${currentState.activeBonusTurnPieceId})`);

        const gameHasChanged = changes.length > 0;

        if (gameHasChanged) {
          // Game state actually changed (new move, player joined, state transition, etc.)
          // Full replacement is necessary to pick up new board positions
          this.gameStateManager.setGameState(convertFromFirebaseState(data));
          this.notifyLocalListeners();
        } else {
          // Metadata-only update (presence heartbeat, tab visibility, time sync, etc.)
          // Merge metadata fields into existing state WITHOUT replacing piece objects
          const existingState = this.gameStateManager.getGameState();
          if (data.lightPlayerLastSeen !== undefined) existingState.lightPlayerLastSeen = data.lightPlayerLastSeen;
          if (data.darkPlayerLastSeen !== undefined) existingState.darkPlayerLastSeen = data.darkPlayerLastSeen;
          if (data.lightPlayerTabHidden !== undefined) existingState.lightPlayerTabHidden = data.lightPlayerTabHidden;
          if (data.darkPlayerTabHidden !== undefined) existingState.darkPlayerTabHidden = data.darkPlayerTabHidden;
          this.notifyLocalListeners();
        }
      }
    );
  }

  async sendUpdateToRemote(moveMade: boolean = false): Promise<void> {
    if (this.disableRemoteSync) return; // Skip if remote sync is disabled (testing)
    if (this.gameStateManager.getGameState().mode !== Mode.Live) return;
    if (!this.gameId) throw Error("Game id not set");

    // Lazy import Firebase functions only when needed for multiplayer
    const { updateGamesDocument } = await import("../../firebase/firebase");
    const { convertToFirebaseState } = await import("../../firebase/firebaseConverter");
    const { State } = await import("../types");

    // Only include time control data if game is in Playing state
    // During piece placement, time control hasn't been initialized yet
    const gameState = this.gameStateManager.getGameState();
    let timeControlData: any = undefined;

    if (gameState.state === State.Playing) {
      const TimeControlManager = (await import("../../timeControl/TimeControlManager")).default;
      const timeState = TimeControlManager.getState();

      // Only include time control data if it's actually been initialized (not OFF)
      if (timeState.config.preset !== 'off') {
        timeControlData = {
          preset: timeState.config.preset,
          lightTimeRemaining: timeState.playerTime.lightTimeRemaining,
          darkTimeRemaining: timeState.playerTime.darkTimeRemaining,
          lastMoveTimestamp: Date.now()
        };
      }
    }

    const firebaseState = convertToFirebaseState(
      gameState,
      moveMade,
      this.lightElo,
      this.darkElo,
      timeControlData
    );
    try {
      await updateGamesDocument(this.gameId!, firebaseState);
    } catch (error: any) {
      throw error; // Re-throw so caller knows it failed!
    }
  }

  ungracefulTermination() {
    if (this.gameId) {
      this.gameStateManager.setGameStateState(State.Terminated);
    }
  }

  // ==========================================
  // Engine Event System (NEW - SOC Phase 1)
  // ==========================================
  // These methods handle internal engine events (e.g., selectPiece)
  // that need to be communicated to the UI layer via EventBridge

  /**
   * Subscribe to engine events (for EventBridge in UI layer)
   * Returns unsubscribe function
   */
  subscribe(listener: ZodiacEngineEventListener): () => void {
    this.engineEventListeners.add(listener);
    return () => this.unsubscribe(listener);
  }

  /**
   * Unsubscribe from engine events
   */
  unsubscribe(listener: ZodiacEngineEventListener): void {
    this.engineEventListeners.delete(listener);
  }

  /**
   * Emit an engine event to all subscribers (UI layer)
   * This is called by engine components instead of document.dispatchEvent()
   */
  emitEvent(event: ZodiacEngineEvent): void {
    // Notify all engine event listeners (primarily EventBridge)
    this.engineEventListeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
      }
    });
  }

  /**
   * Notify all game state listeners (existing functionality)
   * Kept separate from engine events for clarity
   */
  notifyListeners(): void {
    this.notifyLocalListeners();
  }

  /**
   * Broadcast a king guess taunt event (AI or player)
   * Used by the AI to announce its king guess to the UI
   */
  broadcastKingGuess(
    guess: { pieceId: string; position: Position },
    guesserIsLight: boolean
  ): void {
    const event: KingGuessEvent = {
      type: EventType.kingGuess,
      guess,
      guesserIsLight,
      timestamp: Date.now(),
    };

    this.emitEvent(event);
  }
}
